import { Injectable, computed, inject, signal } from '@angular/core';
import { ExternalRepliesStore } from '../../assigned-comments/store/external-replies.store';
import type { WorkflowUiModel } from '../../../core/types';
import { CompanyUsersService, I18nService } from '../../../core';
import { OnboardingChecklistStore } from '../../onboarding/stores/onboarding-checklist.store';
import { WorkflowsStore } from '../stores/workflows.store';
import { ProjectsStore } from '../../projects/stores/projects.store';
import { WorkflowItemsService } from '../services/workflow-items.service';
import type { WorkflowListItemModel } from '../models/workflow-list-item.model';
import type {
  CardQuickFieldViewModel,
  CardSummaryViewModel,
  WorkflowBoardViewModel,
  WorkflowStageViewModel,
} from '../models/workflow-board.model';
import type { ItemPriority, ItemStatus, WorkflowItem } from '../models/workflow-item.model';
import { isGuidString } from '../../../core/utils/guid.util';
import {
  formatFileFieldValueForList,
  getInitials,
  looksLikeFileFieldJsonString,
} from '../../../shared/utils';
import { CustomFieldsDataService } from '../../custom-fields/services/custom-fields-data.service';
import { formatFormulaResultForDisplay } from '../../custom-fields/models/formula-field.model';
import { formatRatingForQuickView, normalizeRatingValue } from '../../custom-fields/utils/rating-field.util';

@Injectable({ providedIn: 'root' })
export class WorkflowsFacade {
  readonly pendingReplyCardIds = inject(ExternalRepliesStore).pendingCardIds;
  deleteCards(cardIds: string[]): Promise<void> {
    return this.workflowItemsService.deleteCards(cardIds);
  }

  private readonly i18n = inject(I18nService);
  private readonly workflowsStore = inject(WorkflowsStore);
  private readonly projectsStore = inject(ProjectsStore);
  private readonly workflowItemsService = inject(WorkflowItemsService);
  private readonly customFieldsData = inject(CustomFieldsDataService);
  private readonly companyUsers = inject(CompanyUsersService);
  private readonly checklistStore = inject(OnboardingChecklistStore);
  private readonly t = this.i18n.t.bind(this.i18n);

  private readonly workflowQuerySignal = signal('');

  private readonly selectedWorkflowIdSignal = signal<string | null>(null);
  private readonly boardSearchQuerySignal = signal('');
  private readonly boardResponsibleFilterSignal = signal<string>('all');
  private readonly boardPriorityFilterSignal = signal<'all' | ItemPriority>('all');

  readonly workflowQuery = this.workflowQuerySignal.asReadonly();
  readonly boardSearchQuery = this.boardSearchQuerySignal.asReadonly();
  readonly boardResponsibleFilter = this.boardResponsibleFilterSignal.asReadonly();
  readonly boardPriorityFilter = this.boardPriorityFilterSignal.asReadonly();

  readonly loading = computed(() => this.workflowsStore.loading() || this.projectsStore.loading());
  readonly error = computed(() => this.workflowsStore.error() ?? this.projectsStore.error());

  readonly workflows = computed<WorkflowListItemModel[]>(() => {
    const projectById = new Map(
      this.projectsStore.projects().map((project) => [
        project.id,
        {
          name: project.name,
          colorHex: this.resolveProjectAccent(project.name, project.colorHex),
        },
      ] as const)
    );

    return this.workflowsStore
      .workflows()
      .map((workflow) => {
        const workflowName = workflow.name?.trim() || workflow.id || this.t('domain.workflow');
        const cardsCount = this.workflowItemsService.getByWorkflow(workflow.id).length;
        const projectInfo = projectById.get(workflow.projectId);
        const projectName = projectInfo?.name ?? this.t('domain.project');
        return {
          ...workflow,
          name: workflowName,
          iconKey: workflow.iconKey || 'columns-3',
          colorHex: workflow.colorHex || '#3B82F6',
          projectName,
          projectAccentColor:
            projectInfo?.colorHex ?? this.resolveProjectAccent(projectName, null),
          stagesCount: workflow.stages.length,
          cardsCount,
          lastActivity: this.getWorkflowLastActivity(cardsCount),
          activityRank: this.getWorkflowActivityRank(cardsCount),
        };
      });
  });

  readonly filteredWorkflows = computed<WorkflowListItemModel[]>(() => {
    const query = this.workflowQuerySignal().trim().toLowerCase();
    return this.workflows().filter((workflow) =>
      query
        ? workflow.name.toLowerCase().includes(query) ||
          workflow.projectName.toLowerCase().includes(query)
        : true
    );
  });

  readonly isEmpty = computed(
    () => !this.loading() && !this.error() && this.filteredWorkflows().length === 0
  );

  readonly board = computed<WorkflowBoardViewModel | null>(() => {
    const workflowId = this.selectedWorkflowIdSignal();
    if (!workflowId) return null;

    const workflow = this.workflowsStore.getById(workflowId);
    if (!workflow) return null;

    const project = this.projectsStore.getById(workflow.projectId);
    const cards = this.getFilteredBoardItems(workflowId);
    const stages = this.buildStageColumns(workflow, cards);
    const totalCardsCount = stages.reduce((sum, stage) => sum + stage.cardsCount, 0);

    return {
      workflowId: workflow.id,
      workflowName: workflow.name,
      workflowIconKey: workflow.iconKey,
      workflowColorHex: workflow.colorHex,
      projectId: workflow.projectId,
      projectName: project?.name ?? this.t('domain.project'),
      totalCardsCount,
      stages,
    };
  });

  readonly boardAssignees = computed<string[]>(() => {
    const workflowId = this.selectedWorkflowIdSignal();
    if (!workflowId) return [];
    const values = new Set(
      this.workflowItemsService
        .getByWorkflow(workflowId)
        .map((item) => item.assigneeName)
        .filter(Boolean)
    );
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  });

  readonly boardPriorities = computed<ItemPriority[]>(() => this.workflowItemsService.getPriorities());

  setWorkflowQuery(value: string): void {
    this.workflowQuerySignal.set(value);
  }

  clearWorkflowQuery(): void {
    this.workflowQuerySignal.set('');
  }

  retry(): void {
    this.workflowsStore.load();
    this.projectsStore.load();
  }

  /** Recarrega workflows da API e só então o caller pode navegar (etapas/nomes corretos). */
  async reloadWorkflowsAsync(): Promise<void> {
    await this.workflowsStore.reloadAsync();
  }

  selectWorkflow(workflowId: string | null): void {
    this.selectedWorkflowIdSignal.set(workflowId);
    if (workflowId) {
      void this.workflowItemsService.ensureWorkflowLoaded(workflowId);
      void this.companyUsers.ensureLoaded();
    }
  }

  setBoardSearch(value: string): void {
    this.boardSearchQuerySignal.set(value);
  }

  setBoardResponsible(value: string): void {
    this.boardResponsibleFilterSignal.set(value || 'all');
  }

  setBoardPriority(value: 'all' | ItemPriority): void {
    this.boardPriorityFilterSignal.set(value);
  }

  clearBoardFilters(): void {
    this.boardSearchQuerySignal.set('');
    this.boardResponsibleFilterSignal.set('all');
    this.boardPriorityFilterSignal.set('all');
  }

  async moveCardToStage(cardId: string, stageId: ItemStatus): Promise<void> {
    await this.workflowItemsService.moveItemToStatus(cardId, stageId);
    void this.checklistStore.refresh();
  }

  getWorkflowById(workflowId: string | null): WorkflowUiModel | null {
    return this.workflowsStore.getById(workflowId);
  }

  getWorkflowsByProject(projectId: string | null): WorkflowUiModel[] {
    return this.workflowsStore.getByProject(projectId);
  }

  getWorkflowListByProject(projectId: string | null): WorkflowListItemModel[] {
    const source = this.filteredWorkflows();
    if (!projectId) return source;
    return source.filter((workflow) => workflow.projectId === projectId);
  }

  getCardsCountByWorkflow(workflowId: string): number {
    return this.workflowItemsService.getByWorkflow(workflowId).length;
  }

  private getFilteredBoardItems(workflowId: string): WorkflowItem[] {
    let items = this.workflowItemsService.getByWorkflow(workflowId);

    const query = this.boardSearchQuerySignal().trim().toLowerCase();
    if (query) {
      items = items.filter((item) => item.title.toLowerCase().includes(query));
    }

    const responsible = this.boardResponsibleFilterSignal();
    if (responsible !== 'all') {
      items = items.filter((item) => item.assigneeName === responsible);
    }

    const priority = this.boardPriorityFilterSignal();
    if (priority !== 'all') {
      items = items.filter((item) => item.priority === priority);
    }

    return items;
  }

  private buildStageColumns(
    workflow: WorkflowUiModel,
    cards: WorkflowItem[]
  ): WorkflowStageViewModel[] {
    const orderedStages = [...workflow.stages].sort((a, b) => a.order - b.order);
    return orderedStages.map((stage) => {
      const mappedCards = cards.filter((card) => card.status === stage.name).map((card) => this.toCardSummary(card));

      return {
        id: stage.name,
        name: stage.name,
        colorHex: stage.colorHex || '#94A3B8',
        cardsCount: mappedCards.length,
        cards: mappedCards,
      };
    });
  }

  private toCardSummary(card: WorkflowItem): CardSummaryViewModel {
    const quickFields = this.buildQuickFields(card);
    return {
      id: card.id,
      code: card.code ?? null,
      title: card.title,
      assigneeName: card.assigneeName,
      assigneeUserId: card.assigneeUserId ?? null,
      assigneeInitials: getInitials(card.assigneeName, 'US'),
      priority: card.priority,
      dueDate: card.dueDate,
      checklistDone: card.customFields?.['email'] ? 2 : 1,
      checklistTotal: 3,
      tag: card.priority === 'Alta' ? this.t('workflowBoard.card.urgentTag') : null,
      workflowStage: card.status,
      quickFields,
    };
  }

  private buildQuickFields(card: WorkflowItem): CardQuickFieldViewModel[] {
    const formulaDpByFieldId = new Map<string, number>();
    for (const f of this.customFieldsData.getFieldsByWorkflow(card.workflowId)) {
      if (f.type === 'FORMULA') {
        formulaDpByFieldId.set(f.fieldId, f.formulaResultDecimalPlaces ?? 0);
      }
    }

    /** Pills extra: só personalizados; data/prioridade/responsável ficam no layout nativo do tile. */
    const custom = (card.quickFields ?? []).map((field) => {
      const raw = field.value;
      const empty = raw == null || String(raw).trim().length === 0;
      let value: string;
      if (empty) {
        value = '-';
      } else if (field.fieldType === 14) {
        value = formatFormulaResultForDisplay(
          raw,
          formulaDpByFieldId.get(field.workflowFieldId) ?? 0
        );
      } else if (field.fieldType === 15) {
        const def = this.customFieldsData
          .getFieldsByWorkflow(card.workflowId)
          .find((f) => f.fieldId === field.workflowFieldId);
        const max = def?.ratingLevels ?? 5;
        const rv = normalizeRatingValue(raw, max);
        value = formatRatingForQuickView(rv, max);
      } else if (field.fieldType === 12 || (typeof raw === 'string' && looksLikeFileFieldJsonString(raw))) {
        value = formatFileFieldValueForList(
          raw,
          this.t('cards.customFields.fileAttached', undefined, 'arquivo anexado')
        );
      } else {
        const personDisp = this.tryPersonQuickFieldDisplay(
          card.workflowId,
          field.workflowFieldId,
          field.fieldType,
          raw
        );
        value = personDisp ?? String(raw).trim();
      }
      let colorHex: string | null = (field.fieldType === 6 || field.fieldType === 21) ? field.colorHex : null;
      const wf = card.workflowId;
      if (wf) {
        const resolved = this.resolveQuickFieldSelectColorHex(wf, field.workflowFieldId, field.value);
        if (resolved) colorHex = resolved;
        else if ((field.fieldType !== 6 && field.fieldType !== 21)) colorHex = null;
      }

      return {
        label: field.label || field.key,
        value,
        fieldType: field.fieldType,
        colorHex,
        isDefault: false,
      };
    });

    return custom;
  }

  private resolveQuickFieldSelectColorHex(
    workflowId: string,
    workflowFieldId: string,
    raw: unknown
  ): string | null {
    if (!workflowId.trim() || !workflowFieldId.trim()) return null;
    const def = this.customFieldsData
      .getFieldsByWorkflow(workflowId)
      .find((f) => f.fieldId === workflowFieldId);
    if (!def?.options.length) return null;
    if ((def.type !== 'SELECT' && def.type !== 'ROW_COLOR') && def.type !== 'USER') return null;

    const s = raw == null ? '' : String(raw).trim();
    if (!s) return null;

    const norm = (x: string) =>
      x
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

    const sn = norm(s);
    const match = def.options.find(
      (o) => o.value.trim() === s || norm(o.value) === sn || norm(o.label) === sn
    );
    const hex = match?.colorHex?.trim();
    return hex || null;
  }

  private tryPersonQuickFieldDisplay(
    workflowId: string,
    workflowFieldId: string,
    fieldType: number,
    raw: unknown
  ): string | undefined {
    if (!workflowId.trim() || !workflowFieldId.trim()) return undefined;
    const s = raw == null ? '' : String(raw).trim();
    if (!s) return undefined;
    const def = this.customFieldsData.getFieldsByWorkflow(workflowId).find((f) => f.fieldId === workflowFieldId);
    const schemaPerson = def?.type === 'PERSON' || def?.type === 'USER';
    const apiPerson = fieldType === 9 && isGuidString(s);
    if (!schemaPerson && !apiPerson) return undefined;
    return this.companyUsers.resolveUserDisplayName(s);
  }

  private getWorkflowLastActivity(cardsCount: number): string {
    if (cardsCount === 0) return this.t('activity.none');
    if (cardsCount <= 2) return this.t('activity.minutes20');
    if (cardsCount <= 5) return this.t('activity.hour1');
    if (cardsCount <= 10) return this.t('activity.hours3');
    return this.t('activity.updatedyesterday');
  }

  private getWorkflowActivityRank(cardsCount: number): number {
    if (cardsCount === 0) return 0;
    if (cardsCount <= 2) return 4;
    if (cardsCount <= 5) return 3;
    if (cardsCount <= 10) return 2;
    return 1;
  }

  private resolveProjectAccent(name: string, colorHex: string | null | undefined): string {
    const normalized = (colorHex ?? '').trim();
    if (/^#([0-9a-f]{6})$/i.test(normalized)) return normalized;

    const palette = ['#262626', '#5B4AD1', '#2F7A59', '#119E51', '#B7791F', '#295A94'];
    let hash = 0;
    for (const char of name) {
      hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
    }

    return palette[hash % palette.length] ?? palette[0];
  }
}
